import React, { useState } from 'react';
import type {
  PortalResumeFailureReason,
  PortalResumeOutcome,
} from '@/api/portal';
import {
  usePortalActions,
  usePortalBlockPresence,
} from './portalActionsContext';
import { PortalInvite } from './PortalEntryRequestForm';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { resolvePortalText } from '@/utils/portalLegacyDefaults';
import { useInertPreviewProps } from './inertPreview';
import { PORTAL_GHOST_BUTTON_STYLE } from './portalGhostButtonStyle';

/**
 * WEB-1395 — блок `resume_button` портала: «Возобновить подписку».
 *
 * Симметрия cancel_button (WEB-1183): отменивший случайно или передумавший
 * покупатель снимает запланированную отмену сам, не через поддержку. Кнопка
 * видна ТОЛЬКО у подписки с запланированной отменой (cancelAtPeriodEnd=true
 * и статус не canceled) — активной возобновлять нечего, терминально
 * отменённой (WEB-1256) Stripe возобновление не даст (это новая покупка).
 *
 * После успеха провайдер обновляет локальное зеркало (cancelAtPeriodEnd →
 * false): эта кнопка исчезает, блок отмены снова рисует живую кнопку,
 * downsell-блок работает в прежней логике — состояние всех блоков едино,
 * потому что живёт в PublicPortalProvider, а не в блоках.
 *
 * WEB-1256: если период дожил до конца, пока страница была открыта, сервер
 * отвечает 409 subscription_period_ended — печатаем честный текст «период
 * закончился — оформите подписку заново» БЕЗ кнопки повтора (не общий сбой).
 */
export interface ResumeSubscriptionButtonProps {
  label?: string;
  /**
   * WEB-1692 юнит 3 (репорт владельца 24.08 «не хватает настроек заливки
   * внутри кнопки — выбора цвета именно этой заливки. Также выбора размера
   * текста внутри»). Крайний случай группы: сюда приходила ТОЛЬКО надпись.
   *
   * Заливку и цвет надписи собирает ТОТ ЖЕ `buttonSurfaceStyle` (WEB-1651-2),
   * что у кнопок квиза и пейволла — второго механизма для портала не строим
   * (прямое указание карточки). Накладка спредится ПОСЛЕ прежних строк и при
   * пустых полях равна пустому объекту: сегодняшний вид до пикселя.
   */
  surfaceStyle?: React.CSSProperties;
  /** Кегль надписи (общее поле кнопочной семьи). Пусто = прежние 14px. */
  fontSize?: number;
  /**
   * WEB-1905: язык ВОРОНКИ. Фолбэк надписи и все отказы кнопки приходят из
   * словаря плеера по нему — раньше они были английскими константами.
   */
  language?: string | null;
}

const noteStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#8c8c8c',
  marginTop: 4,
};

const failureNoteStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 13,
  color: '#cf1322',
};

function ResumeRow({
  subscriptionId,
  label,
  resume,
  ownStyle,
  language,
}: {
  subscriptionId: string;
  label: string;
  resume: (subscriptionId: string) => Promise<PortalResumeOutcome>;
  ownStyle: React.CSSProperties;
  language?: string | null;
}) {
  const t = getPublicQuizStrings(language).portal;
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<PortalResumeFailureReason | null>(
    null,
  );

  const onResume = async () => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    const outcome = await resume(subscriptionId);
    setBusy(false);
    if (!outcome.ok) {
      setFailure(outcome.reason);
    }
    // Успех отражается провайдером (cancelAtPeriodEnd=false) — строка
    // перерисуется уровнем выше, success-текст печатает родитель.
  };

  // WEB-1256: терминальный отказ — период закончился, повтор бессмыслен.
  if (failure === 'period_ended') {
    return (
      <p
        data-testid={`portal-resume-period-ended-${subscriptionId}`}
        style={failureNoteStyle}
      >
        {t.resumePeriodEnded}
      </p>
    );
  }

  return (
    <div style={{ marginBottom: 8 }}>
      <button
        type="button"
        // WEB-1692: вид от автора — ПОСЛЕ прежних строк; пусто = {}.
        style={{ ...PORTAL_GHOST_BUTTON_STYLE, ...ownStyle, opacity: busy ? 0.6 : 1 }}
        disabled={busy}
        onClick={() => void onResume()}
        data-testid={`portal-resume-${subscriptionId}`}
      >
        {label}
      </button>
      {failure === 'unavailable' && (
        <p style={failureNoteStyle}>{t.resumeError}</p>
      )}
      {/* 'denied' — сессия протухла: провайдер уже переключил hasAccess,
          блок целиком перерисуется в приглашение — локально молчим. */}
    </div>
  );
}

export function ResumeSubscriptionButton({
  label,
  surfaceStyle,
  fontSize,
  language,
}: ResumeSubscriptionButtonProps) {
  const t = getPublicQuizStrings(language).portal;
  const portal = usePortalActions();
  // Та же заявка на приглашение, что у остальных портальных блоков
  // (усечённый реестр видимых экземпляров — см. usePortalBlockPresence).
  const isInviteHost = usePortalBlockPresence();
  // WEB-1905 (остаток приёмки 07.09): старое английское умолчание из
  // содержимого не перебивает словарь (`utils/portalLegacyDefaults.ts`).
  const resolvedLabel = resolvePortalText(label, t.resumeLabel);
  // Успешно возобновлённые подписки этой загрузки: success-текст живёт тут,
  // потому что после успеха условие рендера кнопки (cancelAtPeriodEnd)
  // исчезает вместе со строкой.
  const [resumedIds, setResumedIds] = useState<string[]>([]);
  // WEB-1723: инертность макета без `disabled` — выключенная кнопка гасила
  // клик, и блок нельзя было выбрать в редакторе (см. inertPreview.ts).
  const inertProps = useInertPreviewProps();
  // WEB-1692: заливка/цвет надписи от сборщика вида + свой кегль. Оба пустые —
  // объект пуст, и style кнопки байт-в-байт прежний.
  const ownStyle: React.CSSProperties = {
    ...(surfaceStyle ?? {}),
    ...(fontSize ? { fontSize } : {}),
  };

  if (!portal) {
    // Канвас редактора — кнопка видна (макет), но инертна.
    return (
      <div style={{ marginBottom: 16 }}>
        <button
          type="button"
          style={{ ...PORTAL_GHOST_BUTTON_STYLE, ...ownStyle }}
          {...inertProps}
        >
          {resolvedLabel}
        </button>
      </div>
    );
  }

  // Порядок ранних return'ов — как у CancelSubscriptionButton: loading
  // РАНЬШЕ проверки входа, иначе кнопка мигает «ссылка недействительна».
  if (portal.loading) return null;
  if (!portal.hasAccess) {
    return <PortalInvite active={isInviteHost} language={language} />;
  }
  if (portal.error) {
    return <p style={{ marginBottom: 16 }}>{t.loadError}</p>;
  }
  if (portal.subscriptions.length === 0) {
    return <p style={{ marginBottom: 16 }}>{t.empty}</p>;
  }

  const resume = async (subscriptionId: string) => {
    const outcome = await portal.resume(subscriptionId);
    if (outcome.ok) {
      setResumedIds((prev) =>
        prev.includes(subscriptionId) ? prev : [...prev, subscriptionId],
      );
    }
    return outcome;
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {portal.subscriptions.map((sub) => {
        if (resumedIds.includes(sub.subscriptionId)) {
          return (
            <p
              key={sub.subscriptionId}
              data-testid={`portal-resume-success-${sub.subscriptionId}`}
              style={noteStyle}
            >
              {t.resumeSuccess}
            </p>
          );
        }
        // Видна только у подписки с запланированной отменой: активной
        // возобновлять нечего, терминально отменённая (canceled) — WEB-1256,
        // возобновление невозможно (кнопки нет, честный текст печатает
        // cancel_button/список — «Subscription canceled»).
        if (sub.status === 'canceled' || !sub.cancelAtPeriodEnd) {
          return null;
        }
        return (
          <ResumeRow
            key={sub.subscriptionId}
            subscriptionId={sub.subscriptionId}
            label={resolvedLabel}
            resume={resume}
            ownStyle={ownStyle}
            language={language}
          />
        );
      })}
    </div>
  );
}
